/* eslint-disable max-params */
import { inject, injectable } from '@patsnap/tahiti-core/shared/inversify';
import type { FtoReportTaskCreator, FtoReportTaskData } from '../task-creator';
import { Logger } from '@patsnap/tahiti-core';
import { FtoReportRetrievalResultReportModel } from '../../models/retrieval-result-report-model';
import { FtoReportTitleService } from '../retrieval-result-report-service';
import _ from 'lodash';
import { NOVELTY_CHECK_INPUT_MIN_LENGTH } from '../../constants';
import { I18nService } from '@telecom/shared/services/i18n';
import { FtoReportInputLanguageDetectionService } from '../input-language-detection-service';
import { ToastError } from '@telecom/shared/utils/toast-error';
import { FtoReportCreateTaskApi, type CreateNoveltyCheckTaskRequest } from '../../apis/create-novelty-check-task-api';
import { formatDateToYYYYMMDD } from '../../utils/date-format';

@injectable()
export class FtoReportTaskCreatorImpl implements FtoReportTaskCreator {
    constructor(
        @inject(Logger)
        private readonly _logger: Logger,
        @inject(I18nService)
        private readonly _i18n: I18nService,
        @inject(FtoReportCreateTaskApi)
        private readonly _createNoveltyCheckTaskApi: FtoReportCreateTaskApi,
        @inject(FtoReportRetrievalResultReportModel)
        private readonly _retrievalResultReportModel: FtoReportRetrievalResultReportModel,
        @inject(FtoReportTitleService)
        private readonly _noveltyCheckReportTitleService: FtoReportTitleService,
        @inject(FtoReportInputLanguageDetectionService)
        private readonly _inputLanguageDetectionService: FtoReportInputLanguageDetectionService,
    ) {}

    async createTask(data: FtoReportTaskData): Promise<void> {
        this._logger.info('查新任务创建服务, 开始创建任务');
        this._validateInputLength(data.text);
        // 检测语言
        await this._inputLanguageDetectionService.detect(data.text);
        await this._createNoveltyCheckTaskRepository(data);
        this._retrievalResultReportModel.update((state) => {
            state.techEssential = data.text;
            state.userInput = data.text;
            state.country = data.country;
            state.legalStatus = data.legalStatus;
            state.includeCompany = data.includeCompany;
            state.excludeCompany = data.excludeCompany;
            state.apdStart = data.apdStart;
            state.apdEnd = data.apdEnd;
            return state;
        });
        await this._noveltyCheckReportTitleService.pullNoveltyCheckReportTitle();
        this._logger.info('查新任务创建服务, 创建任务成功');
        return Promise.resolve();
    }

    /**
     * move to repository
     */
    private async _createNoveltyCheckTaskRepository(data: FtoReportTaskData): Promise<void> {
        const request: CreateNoveltyCheckTaskRequest = {
            input: data.text,
            task_id: data.taskId,
            country: data.country,
            legal_status: data.legalStatus,
            include_company: data.includeCompany,
            exclude_company: data.excludeCompany,
            // 将时间对象转化为MMMMYYDD
            apd_start: formatDateToYYYYMMDD(data.apdStart),
            apd_end: formatDateToYYYYMMDD(data.apdEnd),
        };
        await this._createNoveltyCheckTaskApi.createNoveltyCheckTask(request);
        return;
    }

    /**
     * 客户端验证输入长度
     */
    private _validateInputLength(text: string) {
        const size = _.trim(text).length;
        if (size < NOVELTY_CHECK_INPUT_MIN_LENGTH) {
            const message = this._i18n.t('fto.check.inputTooShort');
            throw new ToastError(message);
        }
    }
}
